import * as React from 'react';
import controlStrings from 'owa-strings/owa-common-strings';
import RibbonButton from './RibbonButton';
import strings from 'owa-strings/owa-mail-strings';
import { Callout, DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { Editor } from 'roosterjs-editor-core';
import { FocusZone, IFocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { format } from 'owa-strings';
import { insertTable as insertTableCmd } from 'roosterjs-editor-api';
import assign from 'lodash/assign';

let styles = require('./insertTable.scss');
let classNames = require('classnames/bind').bind(styles);

const INPUT_BOX_DEFAULT_VALUE = '3';
const MAX_TABLE_WIDTH = 10;
const MAX_TABLE_HEIGHT = 10;

const insertTable: RibbonButton = {
    key: 'insertTable',
    title: () => strings.insertTable,
    imageSrc: require('../svg/table.svg'),
    createDropDownCallback: (target, plugin, dismiss, logUsage) => (
        <Callout
            gapSpace={0}
            target={target}
            isBeakVisible={false}
            directionalHint={DirectionalHint.bottomAutoEdge}
            onDismiss={dismiss}>
            <InsertTablePane
                editor={plugin.getEditor()}
                onInsertTable={(cols, rows) => insertTableCmd(plugin.getEditor(), cols, rows)}
                maxNumCols={MAX_TABLE_HEIGHT}
                maxNumRows={MAX_TABLE_WIDTH}
            />
        </Callout>
    ),
};

export default insertTable;

interface InsertTablePaneState {
    cols: number;
    rows: number;
}

interface InsertTablePaneProps {
    onInsertTable: (cols: number, rows: number) => void;
    editor: Editor;
    maxNumCols: number;
    maxNumRows: number;
}

export class InsertTablePane extends React.Component<InsertTablePaneProps, InsertTablePaneState> {
    private lastTarget: EventTarget;
    private focusZone: IFocusZone;

    constructor(props: InsertTablePaneProps) {
        super(props);
        this.state = {
            cols: 0,
            rows: 0,
        };
    }

    static getDerivedStateFromProps(props: InsertTablePaneProps, state: InsertTablePaneState) {
        return assign(
            {},
            state.cols > props.maxNumCols ? { cols: Math.min(state.cols, props.maxNumCols) } : {},
            state.rows > props.maxNumRows ? { rows: Math.min(state.rows, props.maxNumRows) } : {}
        );
    }

    isCell(element: HTMLElement) {
        return element.classList.contains(styles.tableCell);
    }

    render() {
        let rowSet: JSX.Element[] = [];
        let { rows, cols } = this.state;
        for (let i = 1; i <= this.props.maxNumCols; i++) {
            let cells: JSX.Element[] = [];
            for (let j = 1; j <= this.props.maxNumRows; j++) {
                let className = classNames(styles.tableCell, {
                    hovered: i <= rows && j <= cols,
                });
                let key = `cell_${i}_${j}`;
                cells.push(
                    <div
                        className={className}
                        key={key}
                        id={key}
                        data-is-focusable={true}
                        // TODO #34957 Move to strings
                        aria-label={format(strings.tableMatrixAriaLabel, j, i)}
                        aria-posinset={this.props.maxNumCols * i + j}
                    />
                );
            }
            rowSet.push(
                <div className={styles.tableRow} key={`row_${i}`}>
                    {cells}
                </div>
            );
        }
        let tableDescription =
            rows * cols > 0 ? format(strings.tableMatrix, cols, rows) : strings.insertTable;
        return (
            <div
                className={styles.tablePane}
                onMouseMove={this.onMouseMove}
                onMouseOut={this.onMouseOut}
                onClick={this.onCreateTable}
                onKeyDown={this.onKeyDown}>
                <div className={styles.tableTitle}>{tableDescription}</div>
                <FocusZone
                    defaultActiveElement="col_1_1"
                    elementType="div"
                    direction={FocusZoneDirection.bidirectional}
                    onActiveElementChanged={this.updateSelectionToElement}
                    componentRef={(focusZone: IFocusZone) => (this.focusZone = focusZone)}>
                    {rowSet}
                </FocusZone>
                <div className={styles.customButton} onClick={this.showInsertTableDialog}>
                    {strings.insertTableDialog}
                </div>
            </div>
        );
    }

    private onMouseMove = (e: React.MouseEvent<EventTarget>) => {
        this.updateSelectionToElement(e.target);
    };

    private updateSelectionToElement = (target: EventTarget) => {
        if (target != this.lastTarget && target instanceof HTMLElement && this.isCell(target)) {
            this.lastTarget = target;
            let id = target.id;
            let matches = id.match(/cell_(\w+)_(\w+)/);
            if (matches && matches.length == 3) {
                let row = parseInt(matches[1]);
                let col = parseInt(matches[2]);
                this.setState({
                    rows: row,
                    cols: col,
                });
            }
            if (this.focusZone !== null) {
                this.focusZone.focusElement(target);
            } else {
                throw new Error('Focus zone unset');
            }
        }
    };

    private onMouseOut = () => {
        this.setState({
            rows: 0,
            cols: 0,
        });
    };

    private onKeyDown = e => {
        if (e.key.toLowerCase() === 'enter') {
            this.onCreateTable();
            e.stopPropagation();
        }
    };

    private onCreateTable = () => {
        let { rows, cols } = this.state;
        if (rows * cols > 0 && this.props.onInsertTable) {
            this.props.onInsertTable(cols, rows);
        }
    };

    private showInsertTableDialog = async () => {
        let colInput: HTMLInputElement;
        let rowInput: HTMLInputElement;
        let colStr = INPUT_BOX_DEFAULT_VALUE;
        let rowStr = INPUT_BOX_DEFAULT_VALUE;
        let range = this.props.editor.getSelectionRange();
        if (
            DialogResponse.ok ==
            (await confirm(strings.insertTable, null /*subText*/, false /*resolveImmediately*/, {
                okText: controlStrings.okButton,
                cancelText: controlStrings.cancelButton,
                bodyElement: (
                    <div>
                        <div className={styles.insertTableDialogRow}>
                            <div className={styles.insertTableDialogCell}>
                                {strings.numberOfColumnsLabel}
                            </div>
                            <div className={styles.insertTableDialogCell}>
                                <label>
                                    <input
                                        id="colInput"
                                        role="textbox"
                                        type="text"
                                        maxLength={2}
                                        ref={ref => (colInput = ref)}
                                        onChange={() => (colStr = colInput.value)}
                                        defaultValue={colStr}
                                        className={styles.numInput}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className={styles.insertTableDialogRow}>
                            <div className={styles.insertTableDialogCell}>
                                {strings.numberOfRowsLabel}
                            </div>
                            <div className={styles.insertTableDialogCell}>
                                <input
                                    role="textbox"
                                    type="text"
                                    maxLength={2}
                                    ref={ref => (rowInput = ref)}
                                    onChange={() => (rowStr = rowInput.value)}
                                    defaultValue={rowStr}
                                    className={styles.numInput}
                                />
                            </div>
                        </div>
                    </div>
                ),
                delayCallbackAfterAnimation: true,
                focusSelector: '#colInput',
            }))
        ) {
            let cols = parseInt(colStr);
            let rows = parseInt(rowStr);
            if (rows * cols > 0 && this.props.onInsertTable && !this.props.editor.isDisposed()) {
                this.props.editor.select(range);
                this.props.onInsertTable(cols, rows);
            }
        }
    };
}
